import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  FlatList,
  Switch,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../components/DirectionalIcon';
import { ScreenLayout, Header, ConfirmationModal } from '../components';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface DispatchRulesListScreenProps {
  scope?: 'reply_item' | 'dataset' | 'channel_settings';
  attachedTo?: string;
  attachedToLabel?: string;
}

export const DispatchRulesListScreen: React.FC<DispatchRulesListScreenProps> = ({ scope, attachedTo, attachedToLabel }) => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification, activeTab, setActiveTab } = useAppContext();
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState<any[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingRuleId, setDeletingRuleId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingRuleId, setTogglingRuleId] = useState<string | null>(null);

  const loadRules = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getDispatchRules({ scope, attachedTo, limit: 1000, skip: 0 });
      if (response.statusCode === 1000 && response.data?.items) {
        setRules(response.data.items);
      } else {
        const errorMessage = response.errors?.[0]?.message || 'Failed to load rules';
        showNotification('error', 'Error', errorMessage);
      }
    } catch (error: any) {
      console.error('Error loading dispatch rules:', error);
      showNotification('error', 'Error', error.message || 'Failed to load rules');
    } finally {
      setLoading(false);
    }
  }, [scope, attachedTo, showNotification]);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  const handleCreate = () => {
    navigateTo('dispatch-rule-edit', undefined, { scope, attachedTo, attachedToLabel });
  };

  const handleEdit = (rule: any) => {
    navigateTo('dispatch-rule-edit', rule._id, { scope, attachedTo, attachedToLabel });
  };

  const handleDelete = (ruleId: string) => {
    setDeletingRuleId(ruleId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingRuleId) return;
    try {
      setDeleting(true);
      const response = await apiService.deleteDispatchRule(deletingRuleId);
      if (response.statusCode === 1000) {
        showNotification('success', 'Success', 'Rule deleted successfully.');
        setShowDeleteModal(false);
        setDeletingRuleId(null);
        await loadRules();
      } else {
        const errorMessage = response.errors?.[0]?.message || 'Failed to delete rule';
        showNotification('error', 'Error', errorMessage);
      }
    } catch (error: any) {
      console.error('Error deleting dispatch rule:', error);
      showNotification('error', 'Error', error.message || 'Failed to delete rule');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (rule: any) => {
    try {
      setTogglingRuleId(rule._id);
      const response = await apiService.updateDispatchRule(rule._id, { isActive: !rule.isActive });
      if (response.statusCode === 1000) {
        setRules((prev) => prev.map((r) => (r._id === rule._id ? { ...r, isActive: !rule.isActive } : r)));
      } else {
        const errorMessage = response.errors?.[0]?.message || 'Failed to update rule';
        showNotification('error', 'Error', errorMessage);
      }
    } catch (error: any) {
      console.error('Error toggling dispatch rule:', error);
      showNotification('error', 'Error', error.message || 'Failed to update rule');
    } finally {
      setTogglingRuleId(null);
    }
  };

  const handleBack = () => {
    if (Platform.OS === 'web') {
      window.history.back();
    } else {
      navigateTo('quick-reply-items');
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const renderRule = ({ item: rule }: { item: any }) => (
    <TouchableOpacity style={styles.ruleCard} onPress={() => handleEdit(rule)} activeOpacity={0.7}>
      <View style={styles.ruleContent}>
        <View style={styles.ruleHeader}>
          <View style={styles.ruleInfo}>
            <View style={styles.ruleTitleRow}>
              <Text style={styles.ruleName}>{rule.name}</Text>
              {!rule.isActive && (
                <View style={styles.inactiveBadge}>
                  <Text style={styles.inactiveBadgeText}>Inactive</Text>
                </View>
              )}
            </View>
            <Text style={styles.ruleMeta}>
              {(rule.conditionGroups?.length || 0)} condition {rule.conditionGroups?.length === 1 ? 'group' : 'groups'}
            </Text>
          </View>
          <View style={styles.ruleActions}>
            {togglingRuleId === rule._id ? (
              <ActivityIndicator size="small" color={colors.primary} style={styles.toggleLoading} />
            ) : (
              <Switch
                value={rule.isActive}
                onValueChange={() => handleToggleActive(rule)}
                trackColor={{ false: colors.border, true: `${String(colors.primary)}80` }}
                thumbColor={rule.isActive ? colors.primary : colors.white}
              />
            )}
            <TouchableOpacity
              style={styles.actionButton}
              onPress={(e) => {
                e.stopPropagation();
                handleDelete(rule._id);
              }}
              activeOpacity={0.7}
            >
              <Ionicons name="trash-outline" size={18} color={colors.error} />
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </TouchableOpacity>
  );

  if (loading) {
    return (
      <ScreenLayout
        header={
          <Header
            showNotifications={true}
            showMessages={true}
            onNotificationPress={() => navigateTo('notifications')}
            onMessagePress={() => navigateTo('chat')}
            showNavigation={Platform.OS === 'web'}
            activeTab={activeTab}
            onTabPress={handleTabPress}
          />
        }
        showBottomTabs={Platform.OS !== 'web'}
        onLeftPaneClose={() => {}}
        leftPaneContent="settings"
        onSettingsItemPress={async (itemId) => {
          const { setPendingSettingsItem } = await import('../utils/tempStorage');
          await setPendingSettingsItem(itemId);
          navigateTo('settings');
        }}
        onRightPaneClose={() => {}}
        rightPaneContent={null}
      >
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>Loading rules...</Text>
        </View>
      </ScreenLayout>
    );
  }

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      rightPaneContent={null}
    >
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={styles.headerRow}>
            <TouchableOpacity style={styles.backButton} onPress={handleBack}>
              <DirectionalIcon name="arrow-back" size={24} color={colors.text.primary} />
            </TouchableOpacity>
            <View style={styles.titleContainer}>
              <Text style={styles.title}>Dispatch Rules</Text>
              <Text style={styles.subtitle}>
                {attachedToLabel ? `For "${attachedToLabel}"` : `${rules.length} ${rules.length === 1 ? 'rule' : 'rules'}`}
              </Text>
            </View>
            <TouchableOpacity style={styles.addButton} onPress={handleCreate} activeOpacity={0.8}>
              <Ionicons name="add" size={24} color={colors.white} />
            </TouchableOpacity>
          </View>
        </View>

        {rules.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Ionicons name="git-branch-outline" size={64} color={colors.textLight} />
            <Text style={styles.emptyText}>No rules yet</Text>
            <Text style={styles.emptySubtext}>
              Create a rule to conditionally route this item's response based on time, day, or contact attributes.
            </Text>
            <TouchableOpacity style={styles.emptyButton} onPress={handleCreate} activeOpacity={0.7}>
              <Ionicons name="add-circle-outline" size={20} color={colors.primary} />
              <Text style={styles.emptyButtonText}>Create Rule</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <FlatList
            data={rules}
            renderItem={renderRule}
            keyExtractor={(rule, index) => rule._id || `rule-${index}`}
            contentContainerStyle={styles.listContent}
            showsVerticalScrollIndicator={false}
          />
        )}

        <ConfirmationModal
          visible={showDeleteModal}
          title="Delete Rule"
          message="Are you sure you want to delete this rule? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={confirmDelete}
          onCancel={() => {
            setShowDeleteModal(false);
            setDeletingRuleId(null);
          }}
          confirmButtonColor={colors.error}
          loading={deleting}
        />
      </View>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.lightGray,
  },
  header: {
    padding: spacing.lg,
    backgroundColor: colors.background.surface,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    padding: spacing.xs,
    marginRight: spacing.md,
  },
  titleContainer: {
    flex: 1,
  },
  title: {
    fontSize: typography.fontSize.xl,
    fontFamily: typography.fontFamily.bold,
    color: colors.textDark,
    marginBottom: spacing.xs,
  },
  subtitle: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
  },
  addButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    ...designTokens.shadows.sm,
  },
  listContent: {
    padding: spacing.md,
    paddingBottom: spacing.xl,
  },
  ruleCard: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    padding: spacing.md,
    marginBottom: spacing.md,
    ...designTokens.shadows.sm,
  },
  ruleContent: {
    flex: 1,
  },
  ruleHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  ruleInfo: {
    flex: 1,
    marginRight: spacing.md,
  },
  ruleTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.xs / 2,
  },
  ruleName: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  inactiveBadge: {
    backgroundColor: '#FEF3C7',
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
    borderRadius: designTokens.borderRadius.sm,
  },
  inactiveBadgeText: {
    fontSize: typography.fontSize.xs,
    color: '#D97706',
    fontFamily: typography.fontFamily.medium,
  },
  ruleMeta: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  ruleActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  toggleLoading: {
    width: 51,
  },
  actionButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.lightGray,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl * 2,
  },
  emptyText: {
    fontSize: typography.fontSize.xl,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
    marginTop: spacing.lg,
    marginBottom: spacing.xs,
  },
  emptySubtext: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
    textAlign: 'center',
    marginBottom: spacing.xl,
  },
  emptyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.primary + '15',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: designTokens.borderRadius.md,
    gap: spacing.xs,
  },
  emptyButtonText: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
});
